import type { ElectricityMix, Equipment, Location, MethodId, Scenario, Stage } from "../schema/scenario";
import type { EquipmentTemplate, MethodConfig, TemplateInput } from "../schema/master";
import { ELECTRICITY_PRESETS, PRODUCTION_CHAINS, ROUTES } from "../data/pathways";

/** 모드 전환은 현재 유효 비율을 보존한다. kWh 초깃값은 경계 내 설비 합계에 맞춘다. */
export function switchElectricityMode(mix: ElectricityMix, mode: ElectricityMix["mode"], totalKWh: number): ElectricityMix {
  if (mix.mode === mode) return mix;
  const values = mix.sources.map((row) => mix.mode === "share" ? row.share : row.kWh ?? 0);
  const sum = values.reduce((a, b) => a + b, 0);
  return { ...mix, mode, sources: mix.sources.map((row, i) => {
    const share = sum > 0 ? values[i]! / sum : 0;
    return { ...row, share, ...(mode === "kWh" ? { kWh: totalKWh * share } : {}) };
  }) };
}

export function mismatchedProductionEquipment(scenario: Scenario): Equipment[] {
  return scenario.equipment.filter((e) => e.inBoundary && e.techId && PRODUCTION_CHAINS[e.techId] && e.techId !== scenario.basis.productionTechId);
}

/** 생산 전용 설비만 교체한다. 나머지 설비 활동자료는 유지하고 수소 흐름만 다시 잇는다. */
export function replaceProductionEquipment(scenario: Scenario, techId: string, templates: EquipmentTemplate[], method: MethodConfig): Scenario {
  const chain = PRODUCTION_CHAINS[techId];
  if (!chain) throw new Error("이 생산방식의 기본 설비 구성이 없습니다");
  let current = scenario.basis.batchH2Kg;
  const production = chain.map((id, order) => {
    const template = templates.find((t) => t.id === id);
    if (!template) throw new Error(`설비 템플릿이 없습니다: ${id}`);
    const equipment = instantiateTemplate(template, order, template.h2.role === "passthrough" ? current : scenario.basis.batchH2Kg);
    if (equipment.h2.outKg !== null) current = equipment.h2.outKg;
    return equipment;
  });
  const retained = [...scenario.equipment].sort((a, b) => a.order - b.order).filter((e) => e.templateId === "custom" || !e.techId || !PRODUCTION_CHAINS[e.techId]);
  const equipment = [...production, ...retained].map((e, order) => ({ ...e, order }));
  return applyMethodRules({ ...scenario, exampleId: undefined, basis: { ...scenario.basis, productionTechId: techId, technologyReview: undefined }, equipment: rechainH2(equipment) }, method);
}

let counter = 0;
/** 브라우저,서버 어디서나 충돌 없는 짧은 id */
export function newId(prefix = "id"): string {
  counter += 1;
  const rnd = Math.random().toString(36).slice(2, 8);
  return `${prefix}_${Date.now().toString(36)}${counter.toString(36)}${rnd}`;
}

function inputFromTemplate(t: TemplateInput, kgThrough: number) {
  const amount = t.defaultAmount === null ? null : t.scaling === "perKgH2" ? t.defaultAmount * kgThrough : t.defaultAmount;
  return {
    id: newId("in"),
    key: t.key,
    label: t.label,
    kind: t.kind,
    amount,
    unit: t.unit,
    valueType: amount === null ? ("missing" as const) : ("default" as const),
    required: t.required,
    source: t.source,
    note: undefined,
  };
}

/**
 * 템플릿으로 설비 인스턴스를 만든다.
 * kgIn: 이 설비로 들어오는 수소량(생산 설비면 생산량). 기본값은 여기에 비례해 채운다.
 */
export function instantiateTemplate(t: EquipmentTemplate, order: number, kgIn: number): Equipment {
  const loss = t.h2.role === "passthrough" ? kgIn * t.h2.defaultLossRate : 0;
  const h2 =
    t.h2.role === "producer"
      ? { role: "producer" as const, inKg: null, outKg: kgIn, lossKg: null }
      : t.h2.role === "passthrough"
        ? { role: "passthrough" as const, inKg: kgIn, outKg: kgIn - loss, lossKg: loss }
        : { role: "none" as const, inKg: null, outKg: null, lossKg: null };
  return {
    id: newId("eq"),
    templateId: t.id,
    name: t.name,
    stage: t.stage,
    techId: t.techId,
    location: t.location,
    order,
    inBoundary: t.defaultInBoundary,
    exclusionReason: t.defaultInBoundary ? undefined : "outside_boundary",
    inputs: t.inputs.map((i) => inputFromTemplate(i, kgIn)),
    h2,
  };
}

/** 기타(사용자 정의) 설비 , 빈 껍데기. 입력 항목은 사용자가 추가한다 */
export function customEquipment(order: number, stage: Stage, location: Location, name = "사용자 정의 설비"): Equipment {
  return {
    id: newId("eq"),
    templateId: "custom",
    name,
    stage,
    location,
    order,
    inBoundary: true,
    inputs: [],
    h2: { role: "none", inKg: null, outKg: null, lossKg: null },
  };
}

/** 순서를 다시 매긴다(0..n-1) */
export function renumber(equipment: Equipment[]): Equipment[] {
  return [...equipment].sort((a, b) => a.order - b.order).map((e, i) => ({ ...e, order: i }));
}

export function moveEquipment(equipment: Equipment[], id: string, dir: -1 | 1): Equipment[] {
  const list = renumber(equipment);
  const i = list.findIndex((e) => e.id === id);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= list.length) return list;
  const a = list[i]!;
  const b = list[j]!;
  list[i] = { ...b, order: i };
  list[j] = { ...a, order: j };
  return list;
}

/**
 * 수소 흐름을 순서대로 다시 채운다 , 앞 설비의 산출량이 다음 설비의 투입량이 된다.
 * 손실률은 기존 손실/투입 비율을 유지한다. 사용자가 직접 고친 값(valueType 은 h2 에 없으므로) 은
 * 이 함수를 부르는 쪽(UI: "흐름 다시 맞추기" 버튼)이 결정한다.
 */
export function rechainH2(equipment: Equipment[]): Equipment[] {
  const list = renumber(equipment);
  let current: number | null = null;
  return list.map((e) => {
    if (e.h2.role === "producer") {
      current = (current ?? 0) + (e.h2.outKg ?? 0);
      return e;
    }
    if (e.h2.role === "passthrough" && e.inBoundary && current !== null) {
      const rate = e.h2.inKg && e.h2.lossKg !== null ? e.h2.lossKg / e.h2.inKg : 0;
      const loss = current * rate;
      const next = { ...e, h2: { ...e.h2, inKg: current, lossKg: loss, outKg: current - loss } };
      current = current - loss;
      return next;
    }
    return e;
  });
}

/**
 * 산정방식 규칙을 시나리오에 적용한다.
 * cert: 기능단위 고정, 범위 밖 설비를 강제 제외(cert_scope).
 * general: cert_scope 로 제외됐던 설비를 되살린다.
 */
export function applyMethodRules(scenario: Scenario, method: MethodConfig): Scenario {
  const fu = method.functionalUnit.locked
    ? {
        ...scenario.functionalUnit,
        h2Kg: method.functionalUnit.h2Kg,
        pressureBar: method.functionalUnit.pressureBar ?? scenario.functionalUnit.pressureBar,
        state: method.functionalUnit.state ?? scenario.functionalUnit.state,
        deliveryPoint: method.functionalUnit.deliveryPoint ?? scenario.functionalUnit.deliveryPoint,
      }
    : scenario.functionalUnit;
  const equipment = scenario.equipment.map((e) => {
    if (method.boundary.locked && !method.boundary.includeLocations.includes(e.location)) {
      return { ...e, inBoundary: false, exclusionReason: "cert_scope" as const };
    }
    if (!method.boundary.locked && e.exclusionReason === "cert_scope") {
      return { ...e, inBoundary: true, exclusionReason: undefined };
    }
    return e;
  });
  return { ...scenario, method: method.id, functionalUnit: fu, equipment };
}

export function emptyScenario(opts: { name?: string; productionTechId: string; batchH2Kg?: number; baseYear?: number; gridSourceId?: string }): Scenario {
  const now = new Date().toISOString();
  return {
    schemaVersion: 1,
    name: opts.name ?? "새 시나리오",
    meta: { purpose: "", baseYear: opts.baseYear ?? new Date().getFullYear(), createdAt: now, updatedAt: now },
    method: "general",
    basis: { productionTechId: opts.productionTechId, batchH2Kg: opts.batchH2Kg ?? 100 },
    functionalUnit: { h2Kg: 1, purityPct: 99.97, pressureBar: 200, state: "gas", deliveryPoint: "생산시설 출하지점" },
    electricity: { mode: "share", sources: [{ sourceId: opts.gridSourceId ?? "grid_kr", share: 1 }] },
    allocation: { policy: "none", byproducts: [] },
    equipment: [],
  };
}

/* ── 경로 조합으로 시나리오 조립 ─────────────────────────────── */

export interface BuildOptions {
  techId: string;
  routeId: string;
  /** 전력원 빠른 선택 id. 없으면 계통 100% */
  electricityPresetId?: string;
  method?: MethodId;
  batchH2Kg?: number;
  name?: string;
  purpose?: string;
  baseYear?: number;
  /** 예시처럼 결정적인 id 가 필요할 때(테스트,파일 비교). 없으면 난수 id */
  idPrefix?: string;
  purityPct?: number;
}

/**
 * 생산방식 + 전력원 + 저장,이송 경로 + 산정방식 → 설비가 채워진 시나리오.
 * 템플릿은 마스터 데이터(관리자가 고친 값)에서 받는다. 수소 흐름은 순서대로 이어 붙인다.
 */
export function buildScenario(opts: BuildOptions, templates: EquipmentTemplate[], techName?: string): Scenario {
  const chain = PRODUCTION_CHAINS[opts.techId];
  if (!chain) throw new Error(`생산 사슬이 정의되지 않은 기술: ${opts.techId}`);
  const route = ROUTES.find((r) => r.id === opts.routeId);
  if (!route) throw new Error(`알 수 없는 경로: ${opts.routeId}`);
  const preset = ELECTRICITY_PRESETS.find((p) => p.id === (opts.electricityPresetId ?? "grid")) ?? ELECTRICITY_PRESETS[0]!;
  const kg = opts.batchH2Kg ?? 100;
  const now = new Date().toISOString();
  const byId = new Map(templates.map((t) => [t.id, t] as const));

  let current = kg;
  const equipment = [...chain, ...route.templateIds].map((id, i) => {
    const t = byId.get(id);
    if (!t) throw new Error(`설비 템플릿이 없습니다: ${id}`);
    const eq = instantiateTemplate(t, i, t.h2.role === "passthrough" ? current : kg);
    if (t.h2.role === "passthrough") current = eq.h2.outKg ?? current;
    if (opts.idPrefix) {
      return { ...eq, id: `${opts.idPrefix}.eq${i + 1}`, inputs: eq.inputs.map((inp, j) => ({ ...inp, id: `${opts.idPrefix}.eq${i + 1}.in${j + 1}` })) };
    }
    return eq;
  });

  const label = `${techName ?? opts.techId}, ${preset.label}, ${route.name}`;
  return {
    schemaVersion: 1,
    name: opts.name ?? label,
    meta: { purpose: opts.purpose ?? `${label} 수소의 기능단위당 온실가스 배출량 산정`, baseYear: opts.baseYear ?? new Date().getFullYear(), createdAt: now, updatedAt: now },
    method: opts.method ?? "general",
    basis: { productionTechId: opts.techId, batchH2Kg: kg },
    functionalUnit: { h2Kg: 1, purityPct: opts.purityPct ?? 99.97, pressureBar: route.fu.pressureBar, state: route.fu.state, deliveryPoint: route.fu.deliveryPoint },
    electricity: { mode: "share", sources: preset.sources.map((s) => ({ ...s })) },
    allocation: { policy: "none", byproducts: [] },
    equipment: renumber(equipment),
  };
}
